'use client';

import { BookOpen, AlertCircle } from 'lucide-react';
import { cn } from '@/lib/utils';
import { CourseCard } from './CourseCard';
import { SkeletonGrid } from '@/components/dashboard/LoadingState';
import GlassPanel from '@/components/dashboard/GlassPanel';
import { Meridian } from '@/components/brand/Meridian';
import type { PublicInstituteCourseOffering } from '@/lib/api/institute';

export interface CourseListProps {
  offerings: PublicInstituteCourseOffering[];
  isLoading: boolean;
  error: Error | null;
  onRefetch?: () => void;
  count: number;
  page: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  className?: string;
}

export function CourseList({
  offerings,
  isLoading,
  error,
  onRefetch,
  count,
  page,
  totalPages,
  onPageChange,
  className,
}: CourseListProps) {
  return (
    <div className={cn('space-y-6', className)}>
      <p className="text-sm text-muted">
        Showing <span className="font-medium text-foreground">{count}</span> courses
      </p>

      {isLoading && (
        <SkeletonGrid count={4} className="grid-cols-1 sm:grid-cols-2" itemClassName="h-48" />
      )}

      {!isLoading && error && (
        <div className="flex flex-col items-center justify-center py-20">
          <AlertCircle className="h-8 w-8 text-red-400" />
          <p className="mt-2 text-sm text-red-600">
            {error?.message ?? 'Failed to load courses'}
          </p>
          {onRefetch && (
            <button
              onClick={onRefetch}
              className="mt-4 rounded-lg bg-primary px-4 py-2 text-sm text-white"
            >
              Retry
            </button>
          )}
        </div>
      )}

      {!isLoading && !error && offerings.length > 0 && (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          {offerings.map((offering) => (
            <CourseCard key={offering.id} offering={offering} />
          ))}
        </div>
      )}

      {!isLoading && !error && offerings.length === 0 && (
        <GlassPanel className="p-12 text-center">
          <Meridian state="empty" width={140} />
          <BookOpen className="mx-auto h-12 w-12 text-muted" />
          <h3 className="mt-4 text-lg font-semibold text-foreground">No courses found</h3>
          <p className="mt-1 text-sm text-ash">
            Try adjusting your search or check back later for new training intakes.
          </p>
        </GlassPanel>
      )}

      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-3">
          <button
            type="button"
            disabled={page <= 1}
            onClick={() => onPageChange(page - 1)}
            className={cn(
              'rounded-lg border px-4 py-2 text-sm font-medium transition-all',
              page <= 1
                ? 'border-neon-cyan/5 bg-card text-muted'
                : 'border-neon-cyan/10 bg-card text-ash hover:bg-panel'
            )}
          >
            Previous
          </button>
          <span className="text-sm text-ash">
            Page {page} of {totalPages}
          </span>
          <button
            type="button"
            disabled={page >= totalPages}
            onClick={() => onPageChange(page + 1)}
            className={cn(
              'rounded-lg border px-4 py-2 text-sm font-medium transition-all',
              page >= totalPages
                ? 'border-neon-cyan/5 bg-card text-muted'
                : 'border-neon-cyan/10 bg-card text-ash hover:bg-panel'
            )}
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
}
